import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, Clock, Shield, Heart, MapPin, Building, CheckCircle, Anchor, Target, TrendingUp, Handshake } from 'lucide-react';

import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

const AboutUs = () => {
  // Constants
  const EMERGENCY_PHONE = '(916) 256-3468';
  const OFFICE_ADDRESS = '901 H Street, Suite 103, Sacramento, CA 95814';

  return (
    <>
      <Helmet>
        <title>About Sailors Bail Bonds | Serving Sacramento Since 2009</title>
        <meta 
          name="description" 
          content="Learn about Sailors Bail Bonds - Sacramento's trusted bail bond service since 2009. Licensed agents, 24/7 availability, and compassionate support for California families." 
        />
        <meta 
          name="keywords" 
          content="about Sailors Bail Bonds, Sacramento bail bondsman, licensed bail agents, bail bonds company" 
        />
      </Helmet>
      <div className="min-h-screen bg-[#F8F9FA]">
        {/* Use Standardized Header Component */}
        <Header />

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#007BFF] via-blue-600 to-blue-700">
          <div className="max-w-7xl mx-auto text-center">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full border-2 border-white/30 mb-8">
              <Building className="w-5 h-5 mr-2 text-white" />
              <span className="font-bold text-base text-white" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                Serving California Families Since 2009
              </span>
            </div>

            <h2 
              className="text-5xl lg:text-6xl font-black text-white mb-8 leading-tight"
              style={{ fontFamily: 'Roboto Slab, serif' }}
            >
              Your Go-To Allies in Bail Bonds
            </h2>

            <p 
              className="text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              In bail bond business since 2009, we've been your go-to allies in bail bonds, serving all California counties with care. Our staff specializes in friendly service and quick processes.
            </p>
          </div>
        </section>

        {/* Company Story */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Story Content */}
              <div className="space-y-6">
                <div className="inline-flex items-center bg-blue-50 border-2 border-[#007BFF]/20 px-4 py-2 rounded-full">
                  <Anchor className="w-5 h-5 text-[#007BFF] mr-2" />
                  <span className="font-bold text-[#007BFF]" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    Our Story
                  </span>
                </div>

                <h3 
                  className="text-4xl font-black text-gray-900"
                  style={{ fontFamily: 'Roboto Slab, serif' }}
                >
                  Guiding Families Through Difficult Times
                </h3>

                <p 
                  className="text-lg text-gray-700 leading-relaxed"
                  style={{ fontFamily: 'Open Sans, sans-serif' }}
                >
                  Just like a sailor navigates through stormy seas to bring their crew safely to shore, we navigate the complex bail system to bring your loved ones home. Since 2009, we've been that steady hand guiding Sacramento families through one of life's most stressful moments.
                </p>

                <p 
                  className="text-lg text-gray-700 leading-relaxed"
                  style={{ fontFamily: 'Open Sans, sans-serif' }}
                >
                  We understand that when someone you care about is in jail, you need more than just paperwork—you need compassion, understanding, and someone who genuinely cares. That's why our team treats every family like our own, offering not just bail bond services, but peace of mind.
                </p>

                <div className="grid grid-cols-2 gap-6 pt-6">
                  <div className="bg-gradient-to-br from-blue-50 to-[#F8F9FA] border-2 border-[#007BFF]/20 rounded-xl p-6">
                    <div className="text-3xl font-black text-[#007BFF] mb-2" style={{ fontFamily: 'Roboto Slab, serif' }}>
                      15+
                    </div>
                    <div className="text-base font-semibold text-gray-700" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      Years Combined Experience
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-[#F8F9FA] border-2 border-[#28A745]/20 rounded-xl p-6">
                    <div className="text-3xl font-black text-[#28A745] mb-2" style={{ fontFamily: 'Roboto Slab, serif' }}>
                      1000+
                    </div>
                    <div className="text-base font-semibold text-gray-700" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      Families Helped
                    </div>
                  </div>
                </div>
              </div>

              {/* Office Image */}
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-blue-50 to-white border-4 border-[#007BFF]/20 rounded-3xl p-8 shadow-2xl">
                  <img 
                    src="/assets/images/Image_10-29-25_at_6.11_AM-1761768945245.jpeg" 
                    alt="Professional office building at 901 H Street, Sacramento with handicap access and ample parking"
                    className="w-full h-auto rounded-2xl shadow-xl"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-lg">
                  {/* Office Building Photo */}
                  <div className="mb-6">
                    <img 
                      src="/assets/images/Image_10-29-25_at_6.08_AM-1764525441489.jpeg" 
                      alt="Sailors Bail Bonds downtown office building located at 901 H Street, Suite 103, Sacramento, California"
                      className="w-full h-auto rounded-lg shadow-md"
                    />
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <MapPin className="w-6 h-6 text-[#007BFF] mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2" style={{ fontFamily: 'Roboto Slab, serif' }}>
                        Visit Our Office
                      </h4>
                      <p className="text-gray-700 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                        {OFFICE_ADDRESS}
                      </p>
                      <p className="text-gray-600 mt-2" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                        Professional office building • Handicap access • Ample parking • Clean, welcoming environment
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-[#F8F9FA]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h3 
                className="text-4xl font-black text-gray-900 mb-4"
                style={{ fontFamily: 'Roboto Slab, serif' }}
              >
                What We Stand For
              </h3>
              <p 
                className="text-xl text-gray-600"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                The values that guide our service every single day
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Value 1 */}
              <div className="bg-white border-2 border-[#007BFF]/20 rounded-2xl p-8 text-center hover:shadow-2xl transition-all transform hover:scale-105">
                <div className="bg-gradient-to-br from-[#007BFF] to-blue-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Heart className="w-10 h-10 text-white" />
                </div>
                <h4 className="text-xl font-black text-gray-900 mb-3" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  Empathy
                </h4>
                <p className="text-base text-gray-700 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  We treat every family like our own, understanding the stress and emotional toll of having a loved one in jail.
                </p>
              </div>

              {/* Value 2 */}
              <div className="bg-white border-2 border-[#28A745]/20 rounded-2xl p-8 text-center hover:shadow-2xl transition-all transform hover:scale-105">
                <div className="bg-gradient-to-br from-[#28A745] to-green-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Target className="w-10 h-10 text-white" />
                </div>
                <h4 className="text-xl font-black text-gray-900 mb-3" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  Transparency
                </h4>
                <p className="text-base text-gray-700 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  No hidden fees, no confusing jargon—just clear, honest communication about costs and processes.
                </p>
              </div>

              {/* Value 3 */}
              <div className="bg-white border-2 border-purple-300 rounded-2xl p-8 text-center hover:shadow-2xl transition-all transform hover:scale-105">
                <div className="bg-gradient-to-br from-purple-600 to-purple-700 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-10 h-10 text-white" />
                </div>
                <h4 className="text-xl font-black text-gray-900 mb-3" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  24/7 Availability
                </h4>
                <p className="text-base text-gray-700 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Emergencies don't wait for business hours—neither do we. We're here whenever you need us, day or night.
                </p>
              </div>

              {/* Value 4 */}
              <div className="bg-white border-2 border-amber-300 rounded-2xl p-8 text-center hover:shadow-2xl transition-all transform hover:scale-105">
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <TrendingUp className="w-10 h-10 text-white" />
                </div>
                <h4 className="text-xl font-black text-gray-900 mb-3" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  Excellence
                </h4>
                <p className="text-base text-gray-700 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  We promise the best service around—our team is kind, understanding, and committed to your peace of mind.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Community Involvement */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#007BFF] via-blue-600 to-blue-700">
          <div className="max-w-5xl mx-auto text-center">
            <h3 
              className="text-4xl font-black text-white mb-6"
              style={{ fontFamily: 'Roboto Slab, serif' }}
            >
              Serving Sacramento and All California Counties
            </h3>
            <p 
              className="text-xl text-blue-100 leading-relaxed mb-8"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              From Sacramento County to Stockton, Modesto, and beyond—we're your local experts with statewide reach. Our team knows the California bail system inside and out, ensuring fast, reliable service no matter where your loved one is being held.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="bg-white/10 backdrop-blur-md border-2 border-white/30 rounded-xl p-6">
                <Building className="w-12 h-12 text-white mx-auto mb-4" />
                <h4 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  Professional Office
                </h4>
                <p className="text-blue-100" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Clean, welcoming environment with ample parking and handicap access
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md border-2 border-white/30 rounded-xl p-6">
                <Handshake className="w-12 h-12 text-white mx-auto mb-4" />
                <h4 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  Community Partners
                </h4>
                <p className="text-blue-100" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Trusted relationships with local jails and law enforcement
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md border-2 border-white/30 rounded-xl p-6">
                <Shield className="w-12 h-12 text-white mx-auto mb-4" />
                <h4 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  Licensed & Insured
                </h4>
                <p className="text-blue-100" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Fully compliant with California bail bond regulations
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <h3 
              className="text-4xl font-black text-gray-900 mb-6"
              style={{ fontFamily: 'Roboto Slab, serif' }}
            >
              Ready to Bring Your Loved One Home?
            </h3>
            <p 
              className="text-xl text-gray-600 mb-8"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Call us now for immediate assistance, or fill out our emergency form and we'll contact you within 2 minutes.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href={`tel:${EMERGENCY_PHONE?.replace(/\D/g, '')}`}
                className="group bg-gradient-to-r from-[#28A745] to-green-600 text-white px-10 py-6 rounded-2xl font-black text-2xl hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105 shadow-2xl flex items-center justify-center space-x-3 border-4 border-green-400"
              >
                <Phone className="w-8 h-8 group-hover:animate-pulse" />
                <span>{EMERGENCY_PHONE}</span>
              </a>

              <Link 
                to="/#emergency-form"
                className="bg-[#007BFF] text-white px-10 py-6 rounded-2xl font-black text-2xl hover:bg-blue-700 transition-all transform hover:scale-105 shadow-2xl flex items-center justify-center space-x-3"
              >
                <CheckCircle className="w-8 h-8" />
                <span>Get Help Online</span>
              </Link>
            </div>
          </div>
        </section>

        {/* Use Standardized Footer Component */}
        <Footer />
      </div>
    </>
  );
};

export default AboutUs;